import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  IconButton,
} from '@mui/material';
import { LockResetOutlined, ArrowBackOutlined } from '@mui/icons-material';
import authService from '../services/authService';
import { extractErrorMessage } from '../utils/errorUtils';
import { validatePassword } from '../utils/passwordValidation';
import PasswordStrengthIndicator from '../components/PasswordStrengthIndicator';
import PasswordTextField from '../components/PasswordTextField';

const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation côté client
    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (oldPassword === newPassword) {
      setError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    const validation = validatePassword(newPassword);
    if (!validation.isValid) {
      setError(validation.errors[0] || 'Le mot de passe ne respecte pas les critères requis');
      return;
    }

    setIsLoading(true);

    try {
      const message = await authService.changePassword({
        oldPassword,
        newPassword,
      });
      console.log('Réponse:', message);
      setSuccess(true);

      // Réinitialiser les champs
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Rediriger vers le profil après 2 secondes
      setTimeout(() => {
        navigate('/profile');
      }, 2000);
    } catch (err: any) {
      const errorMessage = extractErrorMessage(
        err,
        'Erreur lors de la modification du mot de passe'
      );
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
          <ArrowBackOutlined />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Modifier le mot de passe
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Mot de passe modifié avec succès !</strong>
            </Typography>
            <Typography variant="body2">
              Votre mot de passe a été changé. Redirection vers votre profil...
            </Typography>
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <PasswordTextField
            fullWidth
            label="Ancien mot de passe"
            value={oldPassword}
            onChange={(e) => setOldPassword(e.target.value)}
            required
            margin="normal"
            autoFocus
            disabled={success}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: '1.05rem',
              },
            }}
          />

          <Box sx={{ mt: 3 }}>
            <PasswordTextField
              fullWidth
              label="Nouveau mot de passe"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              margin="normal"
              disabled={success}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '1.05rem',
                },
              }}
            />

            <PasswordStrengthIndicator password={newPassword} />
          </Box>

          <PasswordTextField
            fullWidth
            label="Confirmer le nouveau mot de passe"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            margin="normal"
            disabled={success}
            error={confirmPassword !== '' && newPassword !== confirmPassword}
            helperText={
              confirmPassword !== '' && newPassword !== confirmPassword
                ? 'Les mots de passe ne correspondent pas'
                : ''
            }
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                fontSize: '1.05rem',
              },
            }}
          />

          <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading || success}
              startIcon={<LockResetOutlined />}
              sx={{
                flex: 1,
                py: 1.5,
                fontSize: '1.05rem',
                fontWeight: 600,
              }}
            >
              {isLoading ? 'Modification...' : 'Modifier le mot de passe'}
            </Button>

            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate(-1)}
              disabled={isLoading}
              sx={{
                flex: 1,
                py: 1.5,
                fontSize: '1.05rem',
              }}
            >
              Annuler
            </Button>
          </Box>
        </form>

        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
          <Typography variant="body2" color="info.dark" sx={{ fontWeight: 600, mb: 1 }}>
            Conseils de sécurité :
          </Typography>
          <Typography variant="caption" color="info.dark" component="div">
            • Utilisez un mot de passe unique que vous n'utilisez nulle part ailleurs
          </Typography>
          <Typography variant="caption" color="info.dark" component="div">
            • Évitez les informations personnelles facilement devinables
          </Typography>
          <Typography variant="caption" color="info.dark" component="div">
            • Changez régulièrement votre mot de passe
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ChangePasswordPage;
